import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

// Languages
import { useTranslation } from "react-i18next";

// CSS
import "./roomscounter.scss";
import {
  HOTELS,
  PACKAGES,
  ORGANIZED_TOURS,
  PLANES,
  useSearch,
  DYNAMIC_PACKAGES,
} from "../../../../../contexts/SearchContext";
import { message } from "antd";
import {
  addSearchRoom,
  adjustSearchRoomCount,
  removeSearchRoom,
  setSearchKidAge,
} from "../../../../../utils/searchOccupancyDraft";

const RoomsCounter = React.forwardRef(
  (
    {
      setDatePickerOpen,
      isOpen = true,
      onClose,
      position = null,
      /** מיקום absolute מתחת לשדה (מיני־סייט חבילות) — בלי fixed ל־viewport */
      anchoredToParent = false,
      /** אתר פומבי / מיני־סייט: פאנל מעוצב שלא נחתך מתחת לכרטיסי הבית */
      premiumPanel = false,
      productType = "package",
      /** טיוטה מקומית ממנוע החיפוש — בלי לדחוף ל-SearchContext בכל לחיצה */
      roomInfo: roomInfoProp,
      setRoomInfo: setRoomInfoProp,
    },
    ref
  ) => {
  // --- Transelator
  const { t } = useTranslation();
  const {
    searchTab,
    setRoomInfo: setRoomInfoFromContext,
    roomInfo: roomInfoFromContext,
    getInitRoomInfo,
  } = useSearch();
  const roomInfo = roomInfoProp || roomInfoFromContext;
  const setRoomInfo = setRoomInfoProp || setRoomInfoFromContext;
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];

  const isHotelSearch = searchTab === HOTELS;
  const isPackageSearch = searchTab === PACKAGES;
  const isOrganizedTourSearch = searchTab === ORGANIZED_TOURS;
  const isDynamicSearch = searchTab === DYNAMIC_PACKAGES;
  const isFlightSearch = productType === "flight" || searchTab === PLANES;

  const updateRoomData = (newData) => {
    setRoomInfo({ rooms: newData });
  };

  const applyCountChange = (index, field, delta) => {
    const next = adjustSearchRoomCount(rooms, index, field, delta);
    if (next.error === "maxguests") {
      message.error(t("roomscounter.maxguests"));
      return;
    }
    updateRoomData(next.rooms);
  };

  const addRoom = (index) => {
    if (index > 0) {
      const newRoom = getInitRoomInfo(true).rooms[0];
      updateRoomData(addSearchRoom(rooms, newRoom));
    }
  };

  const removeRoom = (index) => {
    updateRoomData(removeSearchRoom(rooms, index));
  };

  const increaseAdults = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "adults", 1);
  };

  const decreaseAdults = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "adults", -1);
  };

  const increaseInfant = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "infants", 1);
  };

  const decreaseInfant = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "infants", -1);
  };

  const increaseKids = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "kids", 1);
  };

  const decreaseKids = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    applyCountChange(index, "kids", -1);
  };

  const addKidAge = (e, kidnumber, index) => {
    updateRoomData(setSearchKidAge(rooms, index, kidnumber, e.target.value));
  };

  // Only add 'open' class on mobile (for slide animation)
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const wrapperClassName = [
    "rooms-counter-wrapper",
    anchoredToParent && !isMobile ? "rooms-counter-wrapper--anchored" : "",
    premiumPanel ? "rooms-counter-wrapper--premium" : "",
    isMobile && isOpen ? "open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const dirLtr =
    typeof document !== "undefined" &&
    String(document.documentElement.getAttribute("dir") || "").toLowerCase() === "ltr";

  let style = {};
  if (anchoredToParent && !isMobile) {
    style = {
      position: "absolute",
      top: "calc(100% + 8px)",
      marginTop: 0,
      ...(dirLtr ? { left: 0, right: "auto" } : { right: 0, left: "auto" }),
      zIndex: 10000020,
      width: premiumPanel ? 400 : 380,
      maxWidth: "min(400px, calc(100vw - 24px))",
      transform: "none",
    };
  } else if (position && !isMobile) {
    style = {
      top: `${position.top}px`,
      right: `${position.right}px`,
      left: "auto",
      position: "fixed",
      zIndex: 99999999,
    };
  } else if (!isMobile) {
    style = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 99999999,
    };
  }
  
  return (
    <div 
      className={wrapperClassName}
      ref={ref}
      style={style}
    >
      <div
        className={`rooms-counter-content ${
          document.documentElement.dir === "ltr"
            ? "rooms-counter-content-left-fix"
            : ""
        }`}
      >
        {rooms.map((room, index) => (
          <div
            className="rooms-counter-room-wrapper"
            key={"rooms-counter-room-wrapper-" + index}
          >
            {!isFlightSearch && (
              <div className="rooms-counter-room-title">
                {t("roomscounter.room")} {index + 1}
              </div>
            )}
            
            {/* Shows only spectated room */}
            <div
              key={index + 1}
              id={`room-number-${index + 1}`}
              className={`rooms-counter-item`}
            >
              {/* Adults counter */}
              <div className="rooms-counter-adults-wrapper">
                <div>
                  <label className="rooms-counter-title">
                    {t("roomscounter.adults")}
                  </label>
                </div>

                <div className="rooms-counter-inc-dec">
                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={(e) => increaseAdults(e, index)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  <div className="rooms-counter-inc-dec-value">
                    {room.adults}
                  </div>

                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={(e) => decreaseAdults(e, index)}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Kid counter */}
              <div className="rooms-counter-kids-wrapper">
                <div className="rooms-counter-text-wrapper">
                  <label className="rooms-counter-title">
                    {t("roomscounter.kids")}
                  </label>
                  <label className="rooms-counter-sub-title">
                    {t("roomscounter.agerestriction")}&nbsp;
                    {isPackageSearch || isDynamicSearch || isOrganizedTourSearch
                      ? "2-12"
                      : "0-17"}
                  </label>
                </div>

                <div className="rooms-counter-inc-dec">
                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={(e) => increaseKids(e, index)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  <div className="rooms-counter-inc-dec-value">{room.kids}</div>

                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={(e) => decreaseKids(e, index)}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Kid Ages */}
              <div className="rooms-counter-kids-info-wrapper">
                {(Array.isArray(room.kidsAges) ? room.kidsAges : []).map((kidAge, kidnumber) => (
                  <div
                    key={kidnumber + 1}
                    className="rooms-counter-kid-wrapper"
                  >
                    <label className="rooms-counter-kid-number">
                      {t("roomscounter.kidnumber")} {kidnumber + 1}
                    </label>

                    <select
                      defaultValue={kidAge}
                      className="rooms-counter-kids-select"
                      onChange={(e) => addKidAge(e, kidnumber, index)}
                    >
                      {isHotelSearch
                        ? Array.from({ length: 18 }, (_, index) => index).map(
                            (optionValue) => (
                              <option key={optionValue} value={optionValue}>
                                {optionValue}
                              </option>
                            )
                          )
                        : Array.from(
                            { length: 11 },
                            (_, index) => index + 2
                          ).map((optionValue) => (
                            <option key={optionValue} value={optionValue}>
                              {optionValue}
                            </option>
                          ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* Infant counter */}
              {(isPackageSearch ||
                isOrganizedTourSearch ||
                isDynamicSearch) && (
                <div className="rooms-counter-adults-wrapper">
                  <div>
                    <label className="rooms-counter-title">
                      {t("roomscounter.infant")}
                    </label>
                  </div>

                  <div className="rooms-counter-inc-dec">
                    <div>
                      <button
                        type="button"
                        className="rooms-counter-button"
                        onClick={(e) => increaseInfant(e, index)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>

                    <div className="rooms-counter-inc-dec-value">
                      {room.infants || 0}
                    </div>

                    <div>
                      <button
                        type="button"
                        className="rooms-counter-button"
                        onClick={(e) => decreaseInfant(e, index)}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Rooms Counter - Mobile only: show after last room */}
            {!isFlightSearch && index === rooms.length - 1 && (
              <div className="rooms-counter-item rooms-counter-rooms-wrapper rooms-counter-rooms-mobile">
                {/* Title */}
                <div>
                  <label className="rooms-counter-title">
                    {t("roomscounter.rooms")}
                  </label>
                </div>

                {/* Add & remove */}
                <div className="rooms-counter-inc-dec">
                  {/* Add */}
                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={() => addRoom(rooms.length + 1)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  {/* Display */}
                  <div className="rooms-counter-inc-dec-value">
                    {rooms.length}
                  </div>

                  {/* Remove */}
                  <div>
                    <button
                      type="button"
                      className="rooms-counter-button"
                      onClick={() => removeRoom(rooms.length - 1)}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Confirm Button - Mobile only: show after rooms counter */}
            {index === rooms.length - 1 && (
              <div className="rooms-counter-confirm-wrapper rooms-counter-confirm-mobile">
                <button
                  type="button"
                  className="rooms-counter-confirm-button"
                  onClick={() => {
                    if (onClose) {
                      onClose();
                    }
                  }}
                >
                  {t("roomscounter.confirm")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rooms-counter-footer rooms-counter-footer-desktop">
        {!isFlightSearch && (
          <div className="rooms-counter-item rooms-counter-rooms-wrapper rooms-counter-rooms-desktop">
            <div>
              <label className="rooms-counter-title">
                {t("roomscounter.rooms")}
              </label>
            </div>

            <div className="rooms-counter-inc-dec">
              <div>
                <button
                  type="button"
                  className="rooms-counter-button"
                      onClick={() => addRoom(rooms.length + 1)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>

              <div className="rooms-counter-inc-dec-value">
                {rooms.length}
              </div>

              <div>
                <button
                  type="button"
                  className="rooms-counter-button"
                  onClick={() => removeRoom(rooms.length - 1)}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="rooms-counter-confirm-wrapper rooms-counter-confirm-desktop">
          <button
            type="button"
            className="rooms-counter-confirm-button"
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
          >
            {t("roomscounter.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
});

RoomsCounter.displayName = 'RoomsCounter';

export default RoomsCounter;
